import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Palette, Type, Save, CalendarClock, ToggleRight, Sparkles, ImageIcon, Upload, X, MessageCircle, FileText } from "lucide-react";
import { FONT_PAIRS, THEMES, applyFontPair, applyTheme, applySettings } from "@/lib/theme";
import { updateSiteSettings, uploadBranding, uploadMenuPdf, absoluteUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Switch } from "@/components/ui/switch";

const BRAND_DEFAULTS = { font_pair: "nelum", theme: "nelum" };

const BRANDING_ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,.ico,.svg";

function getMenuPdfCategories(settings) {
  if (Array.isArray(settings.menu_pdf_categories) && settings.menu_pdf_categories.length) {
    return settings.menu_pdf_categories;
  }
  return [{
    id: "food-menu",
    label: "Food Menu",
    pdf_url: settings.menu_pdf_url || "",
    pages: Array.isArray(settings.menu_pdf_pages) ? settings.menu_pdf_pages : [],
  }];
}

function BrandingField({ label, value, onChange, testid, kind, hint, previewClass }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const previewUrl = value ? absoluteUrl(value) : null;

  async function pickFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadBranding(kind, file);
      onChange(res.image_url);
      toast.success(`${label} uploaded — click Save to apply.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="border border-border bg-card p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline text-primary/80">{label}</div>
          {hint && (
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{hint}</p>
          )}
        </div>
        {value && (
          <button
            type="button"
            data-testid={`${testid}-remove`}
            onClick={() => onChange("")}
            className="inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground hover:text-destructive transition-colors"
            title="Revert to the built-in default"
          >
            <X size={11} strokeWidth={1.5} />
            Use default
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={BRANDING_ACCEPT}
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => pickFile(e.target.files?.[0])}
      />

      <div className="mt-5 flex items-center gap-5">
        <div
          className={`shrink-0 border border-border bg-background/60 flex items-center justify-center overflow-hidden ${previewClass || "w-24 h-24"}`}
          data-testid={`${testid}-preview`}
        >
          {previewUrl ? (
            <img src={previewUrl} alt={label} className="w-full h-full object-contain" />
          ) : (
            <ImageIcon size={22} strokeWidth={1.5} className="text-muted-foreground/50" />
          )}
        </div>
        <button
          type="button"
          data-testid={`${testid}-upload`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
        >
          <Upload size={12} strokeWidth={1.5} />
          {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
        </button>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [saving, setSaving] = useState(false);
  const [pendingFont, setPendingFont] = useState(settings.font_pair);
  const [pendingTheme, setPendingTheme] = useState(settings.theme);
  const [pendingOpeningAt, setPendingOpeningAt] = useState(settings.opening_at || "");
  const [pendingOpeningLabel, setPendingOpeningLabel] = useState(settings.opening_label || "");
  const [pendingCountdownEnabled, setPendingCountdownEnabled] = useState(
    settings.countdown_enabled !== false,
  );
  const [pendingLogoTint, setPendingLogoTint] = useState(settings.logo_tint !== false);
  const [pendingLogoUrl, setPendingLogoUrl] = useState(settings.logo_url || "");
  const [pendingFaviconUrl, setPendingFaviconUrl] = useState(settings.favicon_url || "");
  const [pendingWhatsappEnabled, setPendingWhatsappEnabled] = useState(settings.whatsapp_enabled !== false);
  const [pendingWhatsappNumber, setPendingWhatsappNumber] = useState(settings.whatsapp_number || "");
  const [pendingWhatsappMessage, setPendingWhatsappMessage] = useState(settings.whatsapp_message || "");
  const [pendingWhatsappLabel, setPendingWhatsappLabel] = useState(settings.whatsapp_label || "");
  const [pendingMenuDisplayMode, setPendingMenuDisplayMode] = useState(settings.menu_display_mode || "existing");
  const [pendingMenuPdfCategories, setPendingMenuPdfCategories] = useState(
    getMenuPdfCategories(settings),
  );
  const [pendingMenuHeroVisible, setPendingMenuHeroVisible] = useState(settings.menu_hero_visible !== false);
  const [uploadingMenuPdf, setUploadingMenuPdf] = useState("");
  const menuPdfInputRefs = useRef({});

  // Keep pending in sync when settings load / refresh
  useEffect(() => {
    setPendingFont(settings.font_pair);
    setPendingTheme(settings.theme);
    setPendingOpeningAt(settings.opening_at || "");
    setPendingOpeningLabel(settings.opening_label || "");
    setPendingCountdownEnabled(settings.countdown_enabled !== false);
    setPendingLogoTint(settings.logo_tint !== false);
    setPendingLogoUrl(settings.logo_url || "");
    setPendingFaviconUrl(settings.favicon_url || "");
    setPendingWhatsappEnabled(settings.whatsapp_enabled !== false);
    setPendingWhatsappNumber(settings.whatsapp_number || "");
    setPendingWhatsappMessage(settings.whatsapp_message || "");
    setPendingWhatsappLabel(settings.whatsapp_label || "");
    setPendingMenuDisplayMode(settings.menu_display_mode || "existing");
    setPendingMenuPdfCategories(getMenuPdfCategories(settings));
    setPendingMenuHeroVisible(settings.menu_hero_visible !== false);
  }, [settings]);

  // Revert visual preview if user leaves this screen without saving
  useEffect(() => {
    return () => applySettings(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preview visually only — do NOT mutate context state
  const previewFont = (key) => {
    setPendingFont(key);
    applyFontPair(key);
  };
  const previewTheme = (key) => {
    setPendingTheme(key);
    applyTheme(key);
  };

  // "Set as Default" — snap both font pair and theme to the Nelum brand style.
  function applyBrandDefaults() {
    setPendingFont(BRAND_DEFAULTS.font_pair);
    setPendingTheme(BRAND_DEFAULTS.theme);
    applyFontPair(BRAND_DEFAULTS.font_pair);
    applyTheme(BRAND_DEFAULTS.theme);
    toast.success("Brand defaults applied — click Save to persist.");
  }

  async function pickMenuPdf(categoryId, file) {
    if (!file) return;
    if (file.type && file.type !== "application/pdf") {
      toast.error("Please select a PDF file.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("PDF must be 20 MB or smaller.");
      return;
    }
    setUploadingMenuPdf(categoryId);
    try {
      const res = await uploadMenuPdf(file);
      const nextCategories = pendingMenuPdfCategories.map((category) => (
        category.id === categoryId
          ? { ...category, pdf_url: res.url, pages: res.page_urls || [] }
          : category
      ));
      await updateSiteSettings({
        menu_display_mode: "pdf",
        menu_pdf_url: nextCategories[0]?.pdf_url || "",
        menu_pdf_pages: nextCategories[0]?.pages || [],
        menu_pdf_categories: nextCategories,
      });
      setPendingMenuPdfCategories(nextCategories);
      setPendingMenuDisplayMode("pdf");
      await refresh();
      toast.success("Category PDF uploaded and published.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF upload failed");
    } finally {
      setUploadingMenuPdf("");
      if (menuPdfInputRefs.current[categoryId]) {
        menuPdfInputRefs.current[categoryId].value = "";
      }
    }
  }

  async function save() {
    setSaving(true);
    try {
      await updateSiteSettings({
        font_pair: pendingFont,
        theme: pendingTheme,
        opening_at: pendingOpeningAt,
        opening_label: pendingOpeningLabel,
        countdown_enabled: pendingCountdownEnabled,
        logo_tint: pendingLogoTint,
        logo_url: pendingLogoUrl,
        favicon_url: pendingFaviconUrl,
        whatsapp_enabled: pendingWhatsappEnabled,
        whatsapp_number: pendingWhatsappNumber,
        whatsapp_message: pendingWhatsappMessage,
        whatsapp_label: pendingWhatsappLabel,
        menu_display_mode: pendingMenuDisplayMode,
        menu_pdf_url: pendingMenuPdfCategories[0]?.pdf_url || "",
        menu_pdf_pages: pendingMenuPdfCategories[0]?.pages || [],
        menu_pdf_categories: pendingMenuPdfCategories,
        menu_hero_visible: pendingMenuHeroVisible,
      });
      await refresh(); // updates context + cache
      toast.success("Settings saved · applied site-wide");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setPendingFont(settings.font_pair);
    setPendingTheme(settings.theme);
    setPendingOpeningAt(settings.opening_at || "");
    setPendingOpeningLabel(settings.opening_label || "");
    setPendingCountdownEnabled(settings.countdown_enabled !== false);
    setPendingLogoTint(settings.logo_tint !== false);
    setPendingLogoUrl(settings.logo_url || "");
    setPendingFaviconUrl(settings.favicon_url || "");
    setPendingWhatsappEnabled(settings.whatsapp_enabled !== false);
    setPendingWhatsappNumber(settings.whatsapp_number || "");
    setPendingWhatsappMessage(settings.whatsapp_message || "");
    setPendingWhatsappLabel(settings.whatsapp_label || "");
    setPendingMenuDisplayMode(settings.menu_display_mode || "existing");
    setPendingMenuPdfCategories(getMenuPdfCategories(settings));
    setPendingMenuHeroVisible(settings.menu_hero_visible !== false);
    applySettings(settings);
  }

  const dirty =
    pendingFont !== settings.font_pair ||
    pendingTheme !== settings.theme ||
    pendingOpeningAt !== (settings.opening_at || "") ||
    pendingOpeningLabel !== (settings.opening_label || "") ||
    pendingCountdownEnabled !== (settings.countdown_enabled !== false) ||
    pendingLogoTint !== (settings.logo_tint !== false) ||
    pendingLogoUrl !== (settings.logo_url || "") ||
    pendingFaviconUrl !== (settings.favicon_url || "") ||
    pendingWhatsappEnabled !== (settings.whatsapp_enabled !== false) ||
    pendingWhatsappNumber !== (settings.whatsapp_number || "") ||
    pendingWhatsappMessage !== (settings.whatsapp_message || "") ||
    pendingWhatsappLabel !== (settings.whatsapp_label || "") ||
    pendingMenuDisplayMode !== (settings.menu_display_mode || "existing") ||
    JSON.stringify(pendingMenuPdfCategories) !== JSON.stringify(getMenuPdfCategories(settings)) ||
    pendingMenuHeroVisible !== (settings.menu_hero_visible !== false);

  return (
    <div data-testid="page-admin-settings">
      <main className="mx-auto max-w-[1400px] px-6 lg:px-12 py-14">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="overline">Settings</div>
            <h1 className="mt-4 serif text-4xl md:text-5xl font-light leading-tight text-foreground">
              The house style.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">
              Font pair and color theme apply site-wide. Preview updates live — save to persist.
            </p>
          </div>
          <button
            type="button"
            data-testid="settings-brand-defaults"
            onClick={applyBrandDefaults}
            title="Snap font pair & color theme to the signature Nelum look"
            className="inline-flex items-center gap-2 px-5 py-3 border border-primary/60 text-primary bg-primary/5 text-[0.7rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            <Sparkles size={13} strokeWidth={1.5} />
            Set as Default (Recommended)
          </button>
        </div>

        {/* Opening date + countdown */}
        <section className="mb-16">
          <div className="flex items-center gap-2 overline mb-6">
            <CalendarClock size={12} strokeWidth={1.5} /> Opening date & countdown
          </div>
          <div className="grid md:grid-cols-2 gap-8 border border-border p-6 bg-card">
            <div>
              <label className="overline block mb-3">Opening date &amp; time</label>
              <input
                data-testid="settings-opening-at"
                type="datetime-local"
                className="w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground focus:outline-none focus:border-primary transition-colors font-sans text-sm"
                value={
                  pendingOpeningAt
                    ? new Date(pendingOpeningAt).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) { setPendingOpeningAt(""); return; }
                  // Preserve local timezone in the stored ISO string
                  const localDate = new Date(v);
                  setPendingOpeningAt(localDate.toISOString());
                }}
              />
              <div className="mt-2 text-xs text-muted-foreground">
                Currently: {pendingOpeningAt ? new Date(pendingOpeningAt).toLocaleString() : "—"}
              </div>
            </div>
            <div>
              <label className="overline block mb-3">Overline label</label>
              <input
                data-testid="settings-opening-label"
                type="text"
                className="w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors font-sans text-sm"
                placeholder="Opening 19 August 2026 · Senopati, Jakarta"
                value={pendingOpeningLabel}
                onChange={(e) => setPendingOpeningLabel(e.target.value)}
              />
              <div className="mt-2 text-xs text-muted-foreground">
                Small text above the hero title.
              </div>
            </div>
            <div className="md:col-span-2 flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center gap-3">
                <ToggleRight size={14} strokeWidth={1.5} className={pendingCountdownEnabled ? "text-primary" : "text-muted-foreground"} />
                <div>
                  <div className="text-sm text-foreground/90">Show countdown timer</div>
                  <div className="text-xs text-muted-foreground">Days · Hrs · Min · Sec block under the hero</div>
                </div>
              </div>
              <Switch
                data-testid="settings-countdown-enabled"
                checked={pendingCountdownEnabled}
                onCheckedChange={setPendingCountdownEnabled}
              />
            </div>
          </div>
        </section>

        {/* Menus page display */}
        <section className="mb-16">
          <div className="flex items-center gap-2 overline mb-6">
            <FileText size={12} strokeWidth={1.5} /> Menu display
          </div>
          <div className="border border-border p-6 bg-card space-y-8">
            <div className="flex items-center justify-between gap-6 pb-6 border-b border-border">
              <div>
                <div className="text-sm text-foreground/90">Show menu hero banner</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Show or hide the image and title banner above the menu.
                </div>
              </div>
              <Switch
                data-testid="settings-menu-hero-visible"
                checked={pendingMenuHeroVisible}
                onCheckedChange={setPendingMenuHeroVisible}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <button
                type="button"
                data-testid="settings-menu-mode-existing"
                onClick={() => setPendingMenuDisplayMode("existing")}
                className={`text-left p-6 border transition-colors ${
                  pendingMenuDisplayMode === "existing"
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="overline text-primary/80">Existing menu</div>
                <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                  Show the current interactive menu with categories and individual dishes.
                </p>
              </button>
              <button
                type="button"
                data-testid="settings-menu-mode-pdf"
                onClick={() => (
                  pendingMenuPdfCategories.some((category) => category.pages?.length)
                  && setPendingMenuDisplayMode("pdf")
                )}
                disabled={!pendingMenuPdfCategories.some((category) => category.pages?.length)}
                className={`text-left p-6 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  pendingMenuDisplayMode === "pdf"
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="overline text-primary/80">PDF menu</div>
                <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                  Show uploaded PDF menus as category tabs and interactive books.
                </p>
              </button>
            </div>

            <div className="space-y-4 pt-6 border-t border-border">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-foreground/90">PDF menu categories</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Each category appears as a tab above the menu book.
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="settings-menu-pdf-category-add"
                  disabled={pendingMenuPdfCategories.length >= 8}
                  onClick={() => setPendingMenuPdfCategories((categories) => [
                    ...categories,
                    {
                      id: `menu-${Date.now()}`,
                      label: categories.length === 1 ? "Beverages" : `Menu ${categories.length + 1}`,
                      pdf_url: "",
                      pages: [],
                    },
                  ])}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                >
                  Add category
                </button>
              </div>

              {pendingMenuPdfCategories.map((category, index) => (
                <div
                  key={category.id}
                  data-testid={`settings-menu-pdf-category-${category.id}`}
                  className="border border-border bg-background/35 p-5"
                >
                  <div className="flex flex-col md:flex-row md:items-end gap-4">
                    <label className="flex-1">
                      <span className="block text-[0.65rem] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                        Tab name
                      </span>
                      <input
                        type="text"
                        value={category.label}
                        maxLength={80}
                        data-testid={`settings-menu-pdf-category-label-${index}`}
                        onChange={(event) => setPendingMenuPdfCategories((categories) => (
                          categories.map((item) => (
                            item.id === category.id ? { ...item, label: event.target.value } : item
                          ))
                        ))}
                        className="w-full bg-transparent border border-border px-4 py-3 text-sm text-foreground outline-none focus:border-primary"
                      />
                    </label>

                    <input
                      ref={(element) => {
                        menuPdfInputRefs.current[category.id] = element;
                      }}
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      data-testid={`settings-menu-pdf-input-${index}`}
                      onChange={(event) => pickMenuPdf(category.id, event.target.files?.[0])}
                    />
                    <button
                      type="button"
                      data-testid={`settings-menu-pdf-upload-${index}`}
                      onClick={() => menuPdfInputRefs.current[category.id]?.click()}
                      disabled={Boolean(uploadingMenuPdf)}
                      className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                    >
                      <Upload size={12} strokeWidth={1.5} />
                      {uploadingMenuPdf === category.id
                        ? "Uploading…"
                        : category.pdf_url ? "Replace PDF" : "Upload PDF"}
                    </button>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-4 text-xs">
                    {category.pdf_url ? (
                      <>
                        <a
                          href={absoluteUrl(category.pdf_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Preview PDF
                        </a>
                        <span className="text-muted-foreground">
                          {category.pages?.length || 0} pages
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">No PDF uploaded yet</span>
                    )}
                    <button
                      type="button"
                      data-testid={`settings-menu-pdf-category-remove-${index}`}
                      onClick={() => {
                        const remaining = pendingMenuPdfCategories.filter(
                          (item) => item.id !== category.id,
                        );
                        setPendingMenuPdfCategories(remaining);
                        if (!remaining.some((item) => item.pages?.length)) {
                          setPendingMenuDisplayMode("existing");
                        }
                      }}
                      className="ml-auto inline-flex items-center gap-1.5 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X size={11} strokeWidth={1.5} />
                      Remove category
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex flex-wrap items-center gap-4">
                <span className="text-xs text-muted-foreground">
                  PDF only · maximum 20 MB each · maximum 8 categories
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Floating WhatsApp button */}
        <section className="mb-16">
          <div className="flex items-center gap-2 overline mb-6">
            <MessageCircle size={12} strokeWidth={1.5} /> WhatsApp button
          </div>
          <div className="border border-border p-6 bg-card space-y-8">
            <div className="flex items-center justify-between gap-6 pb-6 border-b border-border">
              <div>
                <div className="text-sm text-foreground/90">Show floating WhatsApp button</div>
                <div className="text-xs text-muted-foreground mt-1">Displayed at the bottom-right of public pages.</div>
              </div>
              <Switch
                data-testid="settings-whatsapp-enabled"
                checked={pendingWhatsappEnabled}
                onCheckedChange={setPendingWhatsappEnabled}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="overline block mb-3">WhatsApp number</label>
                <input
                  data-testid="settings-whatsapp-number"
                  type="tel"
                  className="w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors font-sans text-sm"
                  placeholder="+62 812 3456 7890"
                  value={pendingWhatsappNumber}
                  onChange={(e) => setPendingWhatsappNumber(e.target.value)}
                />
                <div className="mt-2 text-xs text-muted-foreground">Include the country code.</div>
              </div>
              <div>
                <label className="overline block mb-3">Accessibility label</label>
                <input
                  data-testid="settings-whatsapp-label"
                  type="text"
                  className="w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors font-sans text-sm"
                  value={pendingWhatsappLabel}
                  onChange={(e) => setPendingWhatsappLabel(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-3">Prefilled message</label>
                <textarea
                  data-testid="settings-whatsapp-message"
                  rows={3}
                  className="w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors font-sans text-sm resize-y"
                  value={pendingWhatsappMessage}
                  onChange={(e) => setPendingWhatsappMessage(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Brand assets — logo & favicon uploads */}
        <section className="mb-16">
          <div className="flex items-center gap-2 overline mb-6">
            <ImageIcon size={12} strokeWidth={1.5} /> Brand assets
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <BrandingField
              label="Site logo"
              testid="settings-logo-upload"
              kind="logo"
              value={pendingLogoUrl}
              onChange={setPendingLogoUrl}
              hint="PNG, SVG or WebP · max 2 MB. Used in the header, footer and admin. If &lsquo;Follow theme&rsquo; is on, transparent PNG/SVG works best."
              previewClass="w-32 h-24"
            />
            <BrandingField
              label="Favicon"
              testid="settings-favicon-upload"
              kind="favicon"
              value={pendingFaviconUrl}
              onChange={setPendingFaviconUrl}
              hint="ICO, PNG or SVG · max 2 MB. Square, 32×32 or 64×64 works best. Shows in the browser tab."
              previewClass="w-12 h-12"
            />
          </div>
        </section>

        {/* Logo colour behaviour */}
        <section className="mb-16">
          <div className="flex items-center gap-2 overline mb-6">
            <ImageIcon size={12} strokeWidth={1.5} /> Logo colour
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <button
              type="button"
              data-testid="settings-logo-tint-on"
              onClick={() => setPendingLogoTint(true)}
              className={`relative text-left p-6 border transition-all bg-card ${
                pendingLogoTint
                  ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]"
                  : "border-border hover:border-primary/40"
              }`}
            >
              {pendingLogoTint && (
                <div className="absolute top-3 right-3 w-6 h-6 border border-primary flex items-center justify-center text-primary">
                  <Check size={12} strokeWidth={1.5} />
                </div>
              )}
              <div className="overline text-primary/80">Follow theme</div>
              <div className="mt-4 h-20 flex items-center">
                <span
                  aria-hidden="true"
                  className="h-16 w-32 inline-block"
                  style={{
                    backgroundColor: "hsl(var(--primary))",
                    WebkitMaskImage:
                      "url(https://customer-assets.emergentagent.com/job_menu-reserve-27/artifacts/aiozo0pc_nelum-logo-dark.png)",
                    maskImage:
                      "url(https://customer-assets.emergentagent.com/job_menu-reserve-27/artifacts/aiozo0pc_nelum-logo-dark.png)",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "left center",
                    maskPosition: "left center",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                  }}
                />
              </div>
              <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                The logo is recoloured to match the current theme&rsquo;s primary colour.
                Recommended when you switch themes often.
              </p>
            </button>
            <button
              type="button"
              data-testid="settings-logo-tint-off"
              onClick={() => setPendingLogoTint(false)}
              className={`relative text-left p-6 border transition-all bg-card ${
                !pendingLogoTint
                  ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]"
                  : "border-border hover:border-primary/40"
              }`}
            >
              {!pendingLogoTint && (
                <div className="absolute top-3 right-3 w-6 h-6 border border-primary flex items-center justify-center text-primary">
                  <Check size={12} strokeWidth={1.5} />
                </div>
              )}
              <div className="overline text-primary/80">Original colours</div>
              <div className="mt-4 h-20 flex items-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_menu-reserve-27/artifacts/aiozo0pc_nelum-logo-dark.png"
                  alt="NELUM"
                  className="h-16 w-32 object-contain object-left"
                />
              </div>
              <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                The uploaded PNG is shown as-is with its own colours. Use this when your logo has
                a specific brand colour that should never change.
              </p>
            </button>
          </div>
        </section>

        {/* Font pair */}
        <section className="mb-16">
          <div className="flex items-center gap-2 overline mb-6">
            <Type size={12} strokeWidth={1.5} /> Font pair
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(FONT_PAIRS).map(([key, pair]) => {
              const selected = pendingFont === key;
              return (
                <button
                  key={key}
                  data-testid={`settings-font-${key}`}
                  onClick={() => previewFont(key)}
                  className={`relative text-left p-6 border transition-all bg-card ${
                    selected
                      ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]"
                      : "border-border hover:border-primary/40"
                  }`}
                >
                  {selected && (
                    <div className="absolute top-3 right-3 w-6 h-6 border border-primary flex items-center justify-center text-primary">
                      <Check size={12} strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="overline text-primary/80">{pair.label}</div>
                  <div
                    className="mt-3 text-4xl font-light leading-tight text-foreground"
                    style={{ fontFamily: `"${pair.serif}", serif` }}
                  >
                    Modern <span className="italic">Sri Lankan</span>
                  </div>
                  <div
                    className="mt-3 text-sm text-muted-foreground"
                    style={{ fontFamily: `"${pair.sans}", sans-serif` }}
                  >
                    {pair.serif} · {pair.sans}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Theme */}
        <section className="mb-16">
          <div className="flex items-center gap-2 overline mb-6">
            <Palette size={12} strokeWidth={1.5} /> Color theme
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(THEMES).map(([key, t]) => {
              const selected = pendingTheme === key;
              const bg = `hsl(${t.tokens.background})`;
              const primary = `hsl(${t.tokens.primary})`;
              const fg = `hsl(${t.tokens.foreground})`;
              const muted = `hsl(${t.tokens["muted-foreground"]})`;
              return (
                <button
                  key={key}
                  data-testid={`settings-theme-${key}`}
                  onClick={() => previewTheme(key)}
                  className={`relative text-left border transition-all overflow-hidden ${
                    selected
                      ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]"
                      : "border-border hover:border-primary/40"
                  }`}
                >
                  {selected && (
                    <div className="absolute top-3 right-3 w-6 h-6 border border-primary flex items-center justify-center text-primary z-10 bg-background">
                      <Check size={12} strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="p-6" style={{ background: bg }}>
                    <div className="text-[0.65rem] uppercase tracking-[0.22em]" style={{ color: primary }}>
                      {t.label}
                    </div>
                    <div className="mt-3 serif text-2xl leading-tight" style={{ color: fg }}>
                      Nelum <span className="italic" style={{ color: primary }}>Dining</span>
                    </div>
                    <div className="mt-2 text-xs" style={{ color: muted }}>
                      Sample body text
                    </div>
                    <div className="mt-5 flex items-center gap-2">
                      <span className="w-6 h-6" style={{ background: primary }} />
                      <span className="w-6 h-6 border" style={{ borderColor: `hsl(${t.tokens.border})`, background: `hsl(${t.tokens.card})` }} />
                      <span className="w-6 h-6" style={{ background: fg }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border pt-6">
          {dirty && (
            <button
              data-testid="settings-cancel"
              onClick={cancel}
              className="px-5 py-3 text-[0.72rem] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
            >
              Discard
            </button>
          )}
          <button
            data-testid="settings-save"
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <Save size={12} strokeWidth={1.5} />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </main>
    </div>
  );
}
